// Conjuring a spirit (issue 1531; docs/MRCS/shadowrun-3.md §9 step 17): the form behind "Conjure…" in the dice bag,
// only where the table's system names conjuring (so a d20 table never sees it). The conjurer picks a spirit of the
// conjuring family (read by the server), its Force, the conjuring skill (the sheet's row the system names, or a rating
// typed) and how many of its dice to hold back for the drain, with typed TN modifiers for each test. The browser names
// that and nothing the server decides: the server reads the spirit's entry and the conjurer's sheet, throws the
// conjuring and the drain, lands the drain, and puts the spirit's token on the table with its numbers.
import { useEffect, useState } from 'react';
import { CONJURE_LIMITS, magicDiceLines, magicRefusal } from '@dragons-table/vtt-shared';
import { selection } from '../../board/selection';
import { fetchSpirits, fetchTests, type SpiritsAnswer, type TestsAnswer } from '../../entries';
import { asksFor } from './asksFor';
import { useTableState } from '../../table/TableState';
import { useShell } from '../context';
import { BTN } from '../ui';
import { MagicDiceRows, useMagicLines } from './MagicDiceParts';

const btn = BTN;
const field = 'rounded bg-stone-800 px-1 py-0.5 text-xs';

interface TokenLike { id: string; kind: string; name?: string; mine?: boolean; character?: string | null }

const TYPED = 'typed';
const int = (v: string): number | null => (v.trim() !== '' && Number.isInteger(Number(v)) ? Number(v) : null);

export function ConjureForm({ isDm, priv, onClose }: { isDm: boolean; priv: boolean; onClose: () => void }) {
  const { store, act, setNotice, systemRules, scene } = useTableState();
  const { campaignId } = useShell();
  const view = systemRules.conjuring;
  const tokens = Object.values(store.state.objects as Record<string, TokenLike>).filter((o) => o.kind === 'token');
  const mine = tokens.filter((o) => (isDm ? true : o.mine));
  const picked = [...selection.get()].find((id) => mine.some((o) => o.id === id));
  const [who, setWho] = useState(picked ?? mine[0]?.id ?? '');
  const [spirits, setSpirits] = useState<SpiritsAnswer | 'loading'>('loading');
  const [spirit, setSpirit] = useState('0');
  const [answer, setAnswer] = useState<TestsAnswer | 'loading'>(null);
  const [force, setForce] = useState('3');
  const [skill, setSkill] = useState(TYPED);
  const [typedSkill, setTypedSkill] = useState('');
  const [typedRating, setTypedRating] = useState('4');
  const [reserve, setReserve] = useState('0');
  const [extraLabel, setExtraLabel] = useState('');
  const [extraValue, setExtraValue] = useState('0');
  const [drainLabel, setDrainLabel] = useState('');
  const [drainValue, setDrainValue] = useState('0');
  // The conjurer's Magic and drain rating, when its sheet holds none (an NPC the DM plays): the server reads the sheet's first.
  const [typedMagic, setTypedMagic] = useState('');
  const [typedDrain, setTypedDrain] = useState('');

  useEffect(() => {
    let live = true;
    void fetchSpirits(campaignId).then((a) => { if (live) setSpirits(a); });
    return () => { live = false; };
  }, [campaignId]);
  const conjurer = tokens.find((o) => o.id === who);
  useEffect(() => {
    setAnswer(null); setSkill(TYPED);
    if (!conjurer?.character) return undefined;
    let live = true;
    setAnswer('loading');
    void fetchTests(campaignId, who, scene?.id ?? null).then((a) => {
      if (!live) return;
      setAnswer(a);
      // The conjuring skill the system names, else the first skill.
      const skills = (a?.choices ?? []).filter((c) => c.kind === 'skill' || c.kind === 'specialization');
      const named = skills.findIndex((c) => view?.skill && c.ref === view.skill);
      if (named >= 0) setSkill(String(named));
      else if (skills.length) setSkill('0');
    });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [who, conjurer?.character, campaignId]);

  const got = answer && answer !== 'loading' ? answer : null;
  const list = spirits && spirits !== 'loading' ? spirits.spirits : [];
  const chosenSpirit = list[Number(spirit)] ?? null;
  const skills = (got?.choices ?? []).filter((c) => c.kind === 'skill' || c.kind === 'specialization');
  const chosenSkill = skill === TYPED ? null : skills[Number(skill)] ?? null;
  const rating = chosenSkill ? chosenSkill.rating : int(typedRating);
  const skillName = chosenSkill ? chosenSkill.name : typedSkill.trim();
  const forceN = int(force);
  const reserveN = int(reserve) ?? 0;
  // The sheet's own dice (issue 1172): its totem's for a favoured spirit, its element's against the opposed elemental.
  const fighting = (store.state.encounter as { combatants?: Array<{ tokens: string[] }> } | undefined)?.combatants?.some((c) => c.tokens.includes(who)) ?? false;
  const castView = systemRules.spellcasting;
  const magicLines = castView?.magicDice && got?.magic && chosenSpirit ? magicDiceLines(castView.magicDice, got.magic, { for: 'spirit', spirit: chosenSpirit.ref, combat: fighting }) : [];
  const magic = useMagicLines(magicLines);
  // What the conjurer's entry rules out (issue 1172: an elementalist conjures only its own element's elemental).
  const refusal = castView?.magicDice && got?.magic && chosenSpirit ? magicRefusal(castView.magicDice, got.magic, { for: 'spirit', spirit: chosenSpirit.ref }) : null;
  const ready = Boolean(!refusal && view && conjurer && chosenSpirit && forceN && forceN >= 1 && forceN <= CONJURE_LIMITS.force && rating && rating >= 1 && skillName && reserveN >= 0 && reserveN < rating);

  if (!view) return null;
  if (!mine.length) {
    return (
      <div className="rounded border border-stone-700 p-2 text-xs text-stone-400" data-testid="conjure-form">
        You have no token at the table to conjure with.
        <button type="button" className={`${btn} ml-2`} onClick={onClose}>Close</button>
      </div>
    );
  }
  const term = (l: string, v: number) => (l.trim() && v ? [{ label: l.trim().slice(0, CONJURE_LIMITS.label), value: Math.max(-CONJURE_LIMITS.modifier, Math.min(CONJURE_LIMITS.modifier, v)) }] : []);
  return (
    <form className="space-y-1 rounded border border-teal-800 p-2 text-xs" data-testid="conjure-form" onSubmit={(e) => {
      e.preventDefault();
      if (!ready || !chosenSpirit || !forceN || !rating) return;
      const body = {
        kind: 'spirit.conjure', conjurer: who, spirit: { name: chosenSpirit.name.slice(0, CONJURE_LIMITS.name), ref: chosenSpirit.ref, stats: { ...chosenSpirit.stats } }, force: forceN,
        skill: { name: skillName.slice(0, CONJURE_LIMITS.name), rating, ...(chosenSkill?.ref ? { ref: chosenSkill.ref } : {}) }, reserve: reserveN,
        modifiers: [...term(extraLabel, int(extraValue) ?? 0), ...magic.tn].slice(0, CONJURE_LIMITS.modifiers), ...(magic.dice.length ? { dice: magic.dice } : {}), drainModifiers: term(drainLabel, int(drainValue) ?? 0),
        ...(int(typedMagic) !== null && int(typedMagic)! >= 0 ? { magic: Math.min(CONJURE_LIMITS.magic, int(typedMagic)!) } : {}),
        ...(int(typedDrain) !== null && int(typedDrain)! >= 1 ? { drainRating: Math.min(CONJURE_LIMITS.rating, int(typedDrain)!) } : {}),
        private: isDm ? priv : false,
      };
      act(body as never, (reply) => { if (!reply.ok) setNotice(reply.reason ?? 'That spirit could not be conjured.'); else onClose(); });
    }}>
      <div className="flex flex-wrap items-center gap-1">
        <label className="flex items-center gap-1">Conjurer
          <select aria-label="Conjurer" className={field} value={who} onChange={(e) => setWho(e.target.value)}>
            {mine.map((o) => <option key={o.id} value={o.id}>{o.name || 'Unnamed token'}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-1">calls
          <select aria-label="Spirit" className={field} value={spirit} onChange={(e) => setSpirit(e.target.value)} disabled={!list.length}>
            {list.map((s, i) => <option key={s.ref} value={String(i)}>{s.name}</option>)}
          </select>
        </label>
        <label className="flex items-center gap-1">at Force
          <input aria-label="Force" type="number" min={1} max={CONJURE_LIMITS.force} className={`${field} w-12`} value={force} onChange={(e) => setForce(e.target.value)} />
        </label>
        {view.most > 0 && <span className="text-stone-400">at most {view.most} × {view.magic}</span>}
      </div>
      {spirits === 'loading' && <p className="text-stone-400">Reading the spirits…</p>}
      {spirits === null && <p className="text-amber-300">The spirits can't be read just now.</p>}
      {spirits && spirits !== 'loading' && spirits.notice && <p className="text-amber-300">{spirits.notice}</p>}
      {answer === 'loading' && <p className="text-stone-400">Reading the character's sheet…</p>}
      {got?.notice && <p className="text-amber-300">{got.notice}</p>}
      <div className="flex flex-wrap items-center gap-1">
        <label className="flex items-center gap-1">Skill
          <select aria-label="Conjuring skill" className={field} value={skill} onChange={(e) => setSkill(e.target.value)}>
            {skills.map((c, i) => <option key={i} value={String(i)}>{c.name} {c.rating}</option>)}
            <option value={TYPED}>A rating typed in…</option>
          </select>
        </label>
        {skill === TYPED && (
          <>
            <input aria-label="Conjuring skill name" placeholder="Conjuring" maxLength={CONJURE_LIMITS.name} className={`${field} w-24`} value={typedSkill} onChange={(e) => setTypedSkill(e.target.value)} />
            <input aria-label="Conjuring skill rating" type="number" min={1} max={CONJURE_LIMITS.rating} className={`${field} w-12`} value={typedRating} onChange={(e) => setTypedRating(e.target.value)} />
          </>
        )}
        <label className="flex items-center gap-1" title="Dice of the skill held back to resist the drain: the conjuring throws the rest.">held for drain
          <input aria-label="Dice held for the drain" type="number" min={0} max={Math.max(0, (rating ?? 1) - 1)} className={`${field} w-12`} value={reserve} onChange={(e) => setReserve(e.target.value)} />
        </label>
      </div>
      <MagicDiceRows lines={magicLines} state={magic} isDm={isDm} testid="conjure-magic-dice" />
      {refusal && <p className="text-amber-300" data-testid="conjure-refused">{refusal}</p>}
      <div className="flex flex-wrap items-center gap-1">
        <span className="text-stone-400">Conjuring TN</span>
        <input aria-label="Other modifier label" placeholder="foci" maxLength={CONJURE_LIMITS.label} className={`${field} w-20`} value={extraLabel} onChange={(e) => setExtraLabel(e.target.value)} />
        <input aria-label="Other modifier to the TN" type="number" className={`${field} w-12`} value={extraValue} onChange={(e) => setExtraValue(e.target.value)} />
        <span className="text-stone-400">Drain TN</span>
        <input aria-label="Drain modifier label" placeholder="totem" maxLength={CONJURE_LIMITS.label} className={`${field} w-20`} value={drainLabel} onChange={(e) => setDrainLabel(e.target.value)} />
        <input aria-label="Other modifier to the drain TN" type="number" className={`${field} w-12`} value={drainValue} onChange={(e) => setDrainValue(e.target.value)} />
      </div>
      {asksFor(conjurer?.character, answer, 'conjure.magic', 'conjure.drain') && (
        <div className="flex flex-wrap items-center gap-1" data-testid="conjure-typed-conjurer" title="Only when the conjurer's sheet has none.">
          <label className="flex items-center gap-1">{view.magic}
            <input aria-label="Conjurer's Magic" type="number" min={0} max={CONJURE_LIMITS.magic} className={`${field} w-12`} value={typedMagic} onChange={(e) => setTypedMagic(e.target.value)} />
          </label>
          <label className="flex items-center gap-1">{view.drain}
            <input aria-label="Conjurer's drain rating" type="number" min={1} max={CONJURE_LIMITS.rating} className={`${field} w-12`} value={typedDrain} onChange={(e) => setTypedDrain(e.target.value)} />
          </label>
        </div>
      )}
      {chosenSpirit && forceN && rating && <p data-testid="conjure-summary">{chosenSpirit.name} at Force {forceN} · {rating - reserveN} dice vs TN {Math.max(view.minTarget, forceN)} · drain {view.drain}{reserveN ? ` + ${reserveN}` : ''} vs TN {Math.max(view.minTarget, forceN)}</p>}
      <div className="flex gap-1">
        <button type="submit" className={`${btn} border-teal-700`} disabled={!ready}>Conjure</button>
        <button type="button" className={btn} onClick={onClose}>Close</button>
        {isDm && <span className="self-center text-stone-400">{priv ? 'Private: the spirit arrives hidden and the players see nothing.' : 'Public: the table sees it.'}</span>}
      </div>
    </form>
  );
}
